'use client'

import { motion } from 'framer-motion'
import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import SectionTitle from '@/components/ui/SectionTitle'
import Image from '@/components/ui/MediaImage'
import type { PageUrlMap } from '@/types/blog'
import type { ViewAllPageProp } from '@/components/sections/InformationCards'
import { resolveUrlFromMap, buildLocalizedLink } from '@/lib/blog-transformers'
import { buildCategoryUrl, resolveProductTypeKeyForCategoryUrl } from '@/lib/url-helpers'
import { transformMediaUrl } from '@/lib/media-url-transformer'
import { shouldEmbedBlockPropsInDom } from '@/lib/embed-block-props-dom'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'
import { sanitizeCategoryDescriptionHtml } from '@/lib/sanitize-category-description'
import { sanitizeCmsHtml } from '@/lib/sanitize-cms-html'

import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'

const DEFAULT_TITLE = 'Amazing Featured Yacht Package'
const DEFAULT_SUBTITLE = 'Explore the world'

export interface FeaturedYachtCategoryCard {
    id: number
    title: string
    description: string
    imageUrl: string
    href: string
}

interface CategoryApiRow {
    id: number
    name?: string
    slug?: string
    description?: string
    cover_image?: string | null
    search_tab_icon?: string | null
    type?: string
}

function viewAllPageToCardLink(
    page: ViewAllPageProp
): { type: string; value: string; label: string } | undefined {
    if (!page || typeof page !== 'object') return undefined
    if (page.type === 'custom') {
        const u = page.url?.trim()
        if (!u) return undefined
        return { type: 'custom', value: u, label: '' }
    }
    const slug = 'slug' in page && page.slug ? String(page.slug).trim() : ''
    if (slug) {
        const clean = slug.replace(/^\/+/, '').replace(/\/+$/, '')
        if (!clean) return undefined
        return {
            type: 'page',
            value: clean,
            label: 'title' in page && page.title ? String(page.title) : '',
        }
    }
    return undefined
}

export interface HomeFeaturedYachtProps {
    categoryIds?: number[]
    languageId?: number
    language?: string
    title?: string
    subTitle?: string
    description?: string | null
    /** Trait: tüm yat kategorileri / liste sayfası linki */
    viewAllPage?: ViewAllPageProp
    viewAllLabel?: string
    pageUrls?: PageUrlMap
    defaultLanguage?: string
    /** Kategori kartı URL tabanı (yacht, auto → yacht) */
    categoryUrlProductType?: string
    /** SSR / hydrate: seçilen kategoriler (sıra categoryIds ile aynı) */
    initialCategories?: FeaturedYachtCategoryCard[]
    /** false: sunucu verisi veya kategori yok; istemci isteği yapma */
    enableClientFetch?: boolean
    __pageContext?: {
        languageId?: number
        language?: string
        pageUrls?: PageUrlMap
        categoryUrlProductType?: string
    }
}

const PLACEHOLDER_IMAGE =
    'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&h=1200&fit=crop'

export default function HomeFeaturedYacht(props?: HomeFeaturedYachtProps) {
    const {
        categoryIds: propCategoryIds,
        languageId: propLanguageId,
        language: propLanguage,
        title: titleProp,
        subTitle: subTitleProp,
        description: descriptionProp,
        viewAllPage: propViewAllPage,
        viewAllLabel: propViewAllLabel,
        pageUrls: propPageUrls,
        defaultLanguage: propDefaultLanguage = 'en',
        categoryUrlProductType: propCategoryUrlProductType,
        initialCategories: propInitialCategories,
        enableClientFetch: enableClientFetchProp,
        __pageContext,
    } = props || {}
    const enableClientFetch =
        enableClientFetchProp ?? !(propInitialCategories && propInitialCategories.length > 0)

    const { getVariable } = useLanguageVariables()
    const languageId = propLanguageId ?? __pageContext?.languageId
    const languageCode = propLanguage ?? __pageContext?.language ?? 'en'
    const pageUrls = propPageUrls ?? __pageContext?.pageUrls
    const categoryUrlProductType =
        (propCategoryUrlProductType && propCategoryUrlProductType.trim() !== ''
            ? propCategoryUrlProductType.trim()
            : undefined) ??
        __pageContext?.categoryUrlProductType ??
        'yacht'
    const resolvedCatBase =
        categoryUrlProductType === 'auto' ? 'yacht' : categoryUrlProductType

    const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null)
    const [categories, setCategories] = useState<FeaturedYachtCategoryCard[]>(propInitialCategories ?? [])
    const [loading, setLoading] = useState(
        () =>
            !(propInitialCategories && propInitialCategories.length > 0) &&
            Boolean(propCategoryIds && propCategoryIds.length > 0)
    )
    const [error, setError] = useState<string | null>(null)
    const [componentProps, setComponentProps] = useState<{
        categoryIds?: number[]
        title?: string
        subTitle?: string
        description?: string | null
        viewAllPage?: ViewAllPageProp
        viewAllLabel?: string
        categoryUrlProductType?: string
    }>({})

    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return

        const mergeFromDom = () => {
            const componentEl = document.querySelector('[data-component-path="sections/HomeFeaturedYacht"]')
            if (!componentEl) return
            const propsAttr = componentEl.getAttribute('data-component-props')
            if (!propsAttr) return
            try {
                const parsed = JSON.parse(propsAttr) as Record<string, unknown>
                setComponentProps((prev) => {
                    const next = { ...prev }
                    if (Array.isArray(parsed.categoryIds)) {
                        next.categoryIds = parsed.categoryIds as number[]
                    }
                    if (typeof parsed.title === 'string') next.title = parsed.title
                    if (typeof parsed.subTitle === 'string') next.subTitle = parsed.subTitle
                    if ('description' in parsed) {
                        const d = parsed.description
                        next.description =
                            d === null || d === undefined || d === '' ? null : String(d)
                    }
                    if (parsed.viewAllPage !== undefined) {
                        next.viewAllPage = parsed.viewAllPage as ViewAllPageProp
                    }
                    if (typeof parsed.viewAllLabel === 'string') next.viewAllLabel = parsed.viewAllLabel
                    if (typeof parsed.categoryUrlProductType === 'string') {
                        next.categoryUrlProductType = parsed.categoryUrlProductType
                    }
                    return next
                })
            } catch {
                // parse hatası
            }
        }

        mergeFromDom()
        const componentEl = document.querySelector('[data-component-path="sections/HomeFeaturedYacht"]')
        if (!componentEl) return undefined
        const observer = new MutationObserver(mergeFromDom)
        observer.observe(componentEl, { attributes: true, attributeFilter: ['data-component-props'] })

        const handleCategoriesEvent = (event: Event) => {
            const detail = (event as CustomEvent<{ categoryIds?: number[] }>).detail
            if (detail?.categoryIds) {
                setComponentProps((prev) => ({ ...prev, categoryIds: detail.categoryIds }))
            }
        }
        window.addEventListener('bk:categories-selection:changed', handleCategoriesEvent)
        return () => {
            observer.disconnect()
            window.removeEventListener('bk:categories-selection:changed', handleCategoriesEvent)
        }
    }, [])

    const sectionTitle =
        (titleProp && String(titleProp).trim()) ||
        (componentProps.title && String(componentProps.title).trim()) ||
        DEFAULT_TITLE
    const sectionSubTitle =
        (subTitleProp && String(subTitleProp).trim()) ||
        (componentProps.subTitle && String(componentProps.subTitle).trim()) ||
        DEFAULT_SUBTITLE
    const sectionDescription: string | null =
        descriptionProp !== undefined
            ? descriptionProp === null || descriptionProp === ''
                ? null
                : String(descriptionProp)
            : componentProps.description !== undefined
              ? componentProps.description === null || componentProps.description === ''
                  ? null
                  : String(componentProps.description)
              : null

    const viewAllPage = componentProps.viewAllPage ?? propViewAllPage
    const viewAllLabel = componentProps.viewAllLabel ?? propViewAllLabel
    const domCategoryUrlType = componentProps.categoryUrlProductType
    const effectiveCatBase =
        domCategoryUrlType && domCategoryUrlType.trim() !== ''
            ? domCategoryUrlType.trim() === 'auto'
                ? 'yacht'
                : domCategoryUrlType.trim()
            : resolvedCatBase

    const categoryIds = propCategoryIds ?? componentProps.categoryIds

    const transformImageUrl = (url: string | undefined): string => {
        if (!url) return PLACEHOLDER_IMAGE
        return transformMediaUrl(url, null) || url
    }

    const resolveMediaUrl = (value: string) => transformImageUrl(value)

    const normalizeCategoryHref = (row: CategoryApiRow): string => {
        const productType = resolveProductTypeKeyForCategoryUrl(row.type, effectiveCatBase, 'yacht')
        const slug = row.slug?.trim()
        if (slug && pageUrls) {
            return buildCategoryUrl(productType, slug, pageUrls, languageCode, propDefaultLanguage)
        }
        if (slug) {
            const clean = slug.replace(/^\/+/, '')
            return clean ? `/${clean}` : '#'
        }
        const categoryTemplateKey = `${productType}-cat`
        const baseUrl = resolveUrlFromMap(
            pageUrls,
            categoryTemplateKey,
            languageCode,
            propDefaultLanguage,
            `/${productType}`
        )
        return `${baseUrl}/category/${row.id}`
    }

    const mapRowsToCards = (rows: CategoryApiRow[], orderIds: number[]): FeaturedYachtCategoryCard[] => {
        const byId = new Map<number, CategoryApiRow>()
        for (const r of rows) {
            if (typeof r.id === 'number' && !Number.isNaN(r.id)) byId.set(r.id, r)
        }
        return orderIds
            .map((id) => byId.get(id))
            .filter((r): r is CategoryApiRow => r != null)
            .map((row) => {
                const cover = row.cover_image ?? row.search_tab_icon ?? ''
                const imageUrl = transformImageUrl(typeof cover === 'string' ? cover : '')
                return {
                    id: row.id,
                    title: String(row.name ?? ''),
                    description: String(row.description ?? '').trim() || String(row.name ?? ''),
                    imageUrl,
                    href: normalizeCategoryHref(row),
                }
            })
    }

    useEffect(() => {
        if (propInitialCategories && propInitialCategories.length > 0) {
            setCategories(propInitialCategories)
            setLoading(false)
        }
    }, [propInitialCategories])

    const hasInitialData = Boolean(propInitialCategories && propInitialCategories.length > 0)

    useEffect(() => {
        if (!enableClientFetch) {
            setLoading(false)
            return
        }
        if (hasInitialData) {
            setLoading(false)
            return
        }

        async function fetchCategories() {
            if (!categoryIds || categoryIds.length === 0) {
                setCategories([])
                setLoading(false)
                return
            }
            try {
                setLoading(true)
                setError(null)
                const params = new URLSearchParams()
                if (languageId) params.set('language_id', String(languageId))
                params.set('language', languageCode)
                categoryIds.forEach((id) => params.append('category_ids[]', String(id)))
                const response = await fetch(`/api/product-categories?${params.toString()}`)
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}))
                    const errorMessage =
                        (errorData as { message?: string }).message ||
                        (errorData as { error?: string }).error ||
                        `API error: ${response.status}`
                    throw new Error(errorMessage)
                }
                const data = await response.json()
                const raw = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []
                const rows = raw as CategoryApiRow[]
                setCategories(mapRowsToCards(rows, categoryIds))
            } catch (err) {
                console.error('Kategoriler yüklenirken hata:', err)
                setError(err instanceof Error ? err.message : getVariable('categories_load_error', 'Hata'))
                setCategories([])
            } finally {
                setLoading(false)
            }
        }

        fetchCategories()
    }, [categoryIds, languageId, languageCode, hasInitialData, effectiveCatBase, pageUrls, propDefaultLanguage, enableClientFetch])

    const displayCategories = hasInitialData ? (propInitialCategories ?? []) : categories

    const getPageLinkHref = (link?: { type: string; value: string; label: string }) => {
        if (!link) return '#'
        if (link.type === 'custom') return link.value
        if (pageUrls && languageCode && link.value) {
            const baseUrl = resolveUrlFromMap(
                pageUrls,
                link.type === 'page' ? link.value : link.type,
                languageCode,
                propDefaultLanguage,
                link.type === 'page' ? `/${link.value}` : `/${link.type}`
            )
            return buildLocalizedLink(baseUrl, link.value, propDefaultLanguage)
        }
        if (link.type === 'page') {
            const cleanValue = link.value.replace(/^\/+/, '').replace(/\/+$/, '')
            return cleanValue ? `/${cleanValue}` : '#'
        }
        const cleanValue = link.value.replace(/^\/+/, '').replace(/\/+$/, '')
        return cleanValue ? `/${link.type}/${cleanValue}` : '#'
    }

    const viewAllLinkShape = viewAllPageToCardLink(viewAllPage)
    const viewAllHref = getPageLinkHref(viewAllLinkShape)
    const trimmedViewAllLabel = viewAllLabel?.trim()
    const viewAllPageTitle =
        viewAllPage && typeof viewAllPage === 'object' && 'title' in viewAllPage
            ? viewAllPage.title?.trim()
            : undefined
    const viewAllButtonText = trimmedViewAllLabel || viewAllPageTitle || 'VIEW ALL YACHT'
    const showViewAll = Boolean(trimmedViewAllLabel || viewAllLinkShape)

    const sectionShellClass = 'relative bg-alsero-map-decor'

    const titleBlock = (
        <div className="w-full max-w-full px-4 sm:px-6 md:mx-auto md:w-6/12 md:max-w-none md:px-0">
            <SectionTitle
                title={sectionTitle}
                titleTag="h2"
                subTitle={sectionSubTitle}
                subTitleTag="span"
                description={sectionDescription}
                descriptionTag={sectionDescription ? 'p' : null}
            />
        </div>
    )

    if (!loading && displayCategories.length === 0 && categoryIds && categoryIds.length > 0) {
        return (
            <section className={sectionShellClass}>
                <div className="w-full max-w-full px-4 md:w-6/12 md:mx-auto text-center">
                    {titleBlock}
                    <p className="text-gray-500 mt-4">
                        {error || getVariable('categories_no_categories_for_language', 'Bu dil için kategori bulunamadı.')}
                    </p>
                </div>
            </section>
        )
    }

    if (loading) {
        return (
            <section className={sectionShellClass}>
                <div className="w-full max-w-full px-4 md:w-6/12 md:mx-auto text-center">
                    {titleBlock}
                    <p className="text-gray-500 mt-4">{getVariable('yacht.loading', 'Yükleniyor...')}</p>
                </div>
            </section>
        )
    }

    if (!categoryIds || categoryIds.length === 0) {
        return (
            <section className={sectionShellClass}>
                <div className="w-full max-w-full px-4 md:w-6/12 md:mx-auto text-center">
                    {titleBlock}
                    <p className="text-gray-500 mt-4">
                        {getVariable('yacht.please_select_categories', 'Lütfen kategorileri seçin.')}
                    </p>
                </div>
            </section>
        )
    }

    const loopEnabled = displayCategories.length > 3

    return (
        <section className={sectionShellClass}>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="opacity-100"
            >
                {titleBlock}
                <div className="">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="invisible lg:visible lg:col-span-2">
                            <div className="flex items-center h-full justify-end">
                                <button
                                    type="button"
                                    onClick={() => swiperInstance?.slidePrev()}
                                    className="group p-3 sm:p-4 md:p-5 lg:p-[20px] border border-alsero-dark-gray rounded-xl sm:rounded-2xl hover:bg-alsero-text-dark/50 hover:border-alsero-text-dark/20 transition-all ease-in-out cursor-pointer duration-300"
                                    aria-label={getVariable('carousel.prev', 'Önceki')}
                                >
                                    <svg
                                        width="28"
                                        height="25"
                                        viewBox="0 0 28 25"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="fill-alsero-dark-gray group-hover:fill-white"
                                        aria-hidden
                                    >
                                        <path d="M0.447544 13.6676L10.5593 24.5202C10.848 24.83 11.2327 25 11.6429 25C12.0536 25 12.438 24.8297 12.7267 24.5202L13.6448 23.5346C13.9332 23.2253 14.0921 22.8122 14.0921 22.3717C14.0921 21.9314 13.9332 21.5044 13.6448 21.1951L7.7457 14.85L26.4873 14.85C27.3323 14.85 28 14.1401 28 13.2329V11.8396C28 10.9325 27.3323 10.1509 26.4873 10.1509L7.67877 10.1509L13.6445 3.77047C13.933 3.46068 14.0919 3.05879 14.0919 2.61829C14.0919 2.17829 13.933 1.77053 13.6445 1.46099L12.7265 0.478606C12.4378 0.168819 12.0533 -8.34465e-07 11.6427 -8.34465e-07C11.2325 -8.34465e-07 10.8478 0.171018 10.5591 0.480805L0.447317 11.3332C0.157987 11.6439 -0.00113344 12.0588 3.33786e-06 12.4998C-0.000906467 12.9422 0.157985 13.3573 0.447544 13.6676Z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="lg:col-span-8">
                            <Swiper
                                modules={[Navigation]}
                                spaceBetween={16}
                                slidesPerView={1}
                                loop={loopEnabled}
                                onSwiper={(swiper) => setSwiperInstance(swiper)}
                                breakpoints={{
                                    640: { slidesPerView: 2, spaceBetween: 16 },
                                    768: { slidesPerView: 3, spaceBetween: 20 },
                                    1024: { slidesPerView: 3, spaceBetween: 24 },
                                }}
                                className="w-full h-full !pb-4"
                            >
                                {displayCategories.map((card, index) => {
                                    const descHtml = sanitizeCategoryDescriptionHtml(card.description)
                                    return (
                                    <SwiperSlide
                                        key={card.id}
                                        className="py-4 sm:py-6 md:py-8 lg:py-10 px-2 sm:px-3 md:px-4 h-auto"
                                    >
                                        <Link
                                            href={card.href}
                                            className="group flex flex-col h-full overflow-hidden rounded-2xl border border-alsero-light-gray/25 bg-white shadow-sm transition-shadow duration-300 hover:shadow-lg"
                                        >
                                            <div className="relative aspect-[454/240] w-full overflow-hidden bg-alsero-light-gray/20">
                                                <Image
                                                    src={resolveMediaUrl(card.imageUrl)}
                                                    alt={card.title}
                                                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                                                    fill
                                                    sizes="(max-width: 768px) 100vw, 33vw"
                                                    loading={index < 3 ? 'eager' : 'lazy'}
                                                    unoptimized={resolveMediaUrl(card.imageUrl).startsWith('/api/media/proxy/')}
                                                />
                                            </div>
                                            <div className="flex flex-1 flex-col gap-3 p-6 transition-colors duration-300 group-hover:bg-alsero-first group-hover:text-white">
                                                <h3 className="font-sans text-xl font-semibold text-alsero-second group-hover:text-white">
                                                    {card.title}
                                                </h3>
                                                {descHtml ? (
                                                    <div
                                                        className="line-clamp-4 text-base leading-relaxed text-alsero-light-gray group-hover:text-white/95 [&_p]:mb-1 [&_p:last-child]:mb-0 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5"
                                                        dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(descHtml) }}
                                                    />
                                                ) : (
                                                    <p className="line-clamp-4 text-base leading-relaxed text-alsero-light-gray group-hover:text-white/95">
                                                        {card.description}
                                                    </p>
                                                )}
                                            </div>
                                        </Link>
                                    </SwiperSlide>
                                    )
                                })}
                            </Swiper>
                            <nav className="mt-4 flex justify-center gap-4 lg:hidden" aria-label={getVariable('carousel.controls', 'Kategori slider')}>
                                <button
                                    type="button"
                                    onClick={() => swiperInstance?.slidePrev()}
                                    className="group p-3 border border-alsero-dark-gray rounded-xl hover:bg-alsero-text-dark/50 hover:border-alsero-text-dark/20 transition-all duration-300 touch-manipulation"
                                    aria-label={getVariable('carousel.prev', 'Önceki')}
                                >
                                    <svg width="28" height="25" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray group-hover:fill-white" aria-hidden>
                                        <path d="M0.447544 13.6676L10.5593 24.5202C10.848 24.83 11.2327 25 11.6429 25C12.0536 25 12.438 24.8297 12.7267 24.5202L13.6448 23.5346C13.9332 23.2253 14.0921 22.8122 14.0921 22.3717C14.0921 21.9314 13.9332 21.5044 13.6448 21.1951L7.7457 14.85L26.4873 14.85C27.3323 14.85 28 14.1401 28 13.2329V11.8396C28 10.9325 27.3323 10.1509 26.4873 10.1509L7.67877 10.1509L13.6445 3.77047C13.933 3.46068 14.0919 3.05879 14.0919 2.61829C14.0919 2.17829 13.933 1.77053 13.6445 1.46099L12.7265 0.478606C12.4378 0.168819 12.0533 -8.34465e-07 11.6427 -8.34465e-07C11.2325 -8.34465e-07 10.8478 0.171018 10.5591 0.480805L0.447317 11.3332C0.157987 11.6439 -0.00113344 12.0588 3.33786e-06 12.4998C-0.000906467 12.9422 0.157985 13.3573 0.447544 13.6676Z" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => swiperInstance?.slideNext()}
                                    className="group p-3 border border-alsero-dark-gray rounded-xl hover:bg-alsero-text-dark/50 hover:border-alsero-text-dark/20 transition-all duration-300 touch-manipulation"
                                    aria-label={getVariable('carousel.next', 'Sonraki')}
                                >
                                    <svg width="28" height="25" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray group-hover:fill-white" aria-hidden>
                                        <path d="M27.5525 11.3324L17.4407 0.479833C17.152 0.170044 16.7673 2.93169e-06 16.3571 2.85997e-06C15.9464 2.78816e-06 15.562 0.170288 15.2733 0.479832L14.3552 1.46539C14.0668 1.77469 13.9079 2.18782 13.9079 2.62832C13.9079 3.06857 14.0668 3.49562 14.3552 3.80492L20.2543 10.15L1.51267 10.15C0.667668 10.15 -1.89881e-06 10.8599 -2.05742e-06 11.7671L-2.30103e-06 13.1604C-2.45964e-06 14.0675 0.667667 14.8491 1.51267 14.8491L20.3212 14.8491L14.3555 21.2295C14.067 21.5393 13.9081 21.9412 13.9081 22.3817C13.9081 22.8217 14.067 23.2295 14.3555 23.539L15.2735 24.5214C15.5622 24.8312 15.9467 25 16.3573 25C16.7675 25 17.1522 24.829 17.4409 24.5192L27.5527 13.6668C27.842 13.3561 28.0011 12.9412 28 12.5003C28.0009 12.0578 27.842 11.6427 27.5525 11.3324Z" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                        <div className="invisible lg:visible lg:col-span-2">
                            <div className="flex items-center h-full">
                                <button
                                    type="button"
                                    onClick={() => swiperInstance?.slideNext()}
                                    className="group p-3 sm:p-4 md:p-5 lg:p-[20px] border border-alsero-dark-gray rounded-xl sm:rounded-2xl hover:bg-alsero-text-dark/50 hover:border-alsero-text-dark/20 transition-all ease-in-out cursor-pointer duration-300"
                                    aria-label={getVariable('carousel.next', 'Sonraki')}
                                >
                                    <svg
                                        width="28"
                                        height="25"
                                        viewBox="0 0 28 25"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="fill-alsero-dark-gray group-hover:fill-white"
                                        aria-hidden
                                    >
                                        <path d="M27.5525 11.3324L17.4407 0.479833C17.152 0.170044 16.7673 2.93169e-06 16.3571 2.85997e-06C15.9464 2.78816e-06 15.562 0.170288 15.2733 0.479832L14.3552 1.46539C14.0668 1.77469 13.9079 2.18782 13.9079 2.62832C13.9079 3.06857 14.0668 3.49562 14.3552 3.80492L20.2543 10.15L1.51267 10.15C0.667668 10.15 -1.89881e-06 10.8599 -2.05742e-06 11.7671L-2.30103e-06 13.1604C-2.45964e-06 14.0675 0.667667 14.8491 1.51267 14.8491L20.3212 14.8491L14.3555 21.2295C14.067 21.5393 13.9081 21.9412 13.9081 22.3817C13.9081 22.8217 14.067 23.2295 14.3555 23.539L15.2735 24.5214C15.5622 24.8312 15.9467 25 16.3573 25C16.7675 25 17.1522 24.829 17.4409 24.5192L27.5527 13.6668C27.842 13.3561 28.0011 12.9412 28 12.5003C28.0009 12.0578 27.842 11.6427 27.5525 11.3324Z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {showViewAll ? (
                    <div className="mt-14 flex justify-center">
                        <Link
                            href={viewAllHref}
                            data-prop-path="viewAllPage"
                            className="inline-flex items-center gap-3 rounded-md bg-alsero-first px-10 py-4 font-sans text-base font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90"
                        >
                            <span data-prop-path="viewAllLabel">{viewAllButtonText}</span>
                            <svg
                                width="14"
                                height="12"
                                viewBox="0 0 12 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="shrink-0 fill-white"
                                aria-hidden
                            >
                                <path d="M11.8335 3.6178L8.26381 0.157332C8.21395 0.107774 8.1532 0.0681771 8.08544 0.0410843C8.01768 0.0139915 7.94441 0 7.87032 0C7.79624 0 7.72297 0.0139915 7.65521 0.0410843C7.58746 0.0681771 7.5267 0.107774 7.47684 0.157332C7.37199 0.262044 7.31393 0.39827 7.31393 0.539537C7.31393 0.680805 7.37199 0.817024 7.47684 0.921736L10.0943 3.45837H0.55625C0.405122 3.46829 0.26375 3.52959 0.160556 3.62994C0.057363 3.73029 0 3.86225 0 3.99929C0 4.13633 0.057363 4.26829 0.160556 4.36864C0.26375 4.46899 0.405122 4.53029 0.55625 4.54021H10.0927L7.47527 7.07826C7.37042 7.18298 7.31235 7.3192 7.31235 7.46047C7.31235 7.60174 7.37042 7.73796 7.47527 7.84267C7.52513 7.89223 7.58588 7.93182 7.65364 7.95892C7.7214 7.98601 7.79467 8 7.86875 8C7.94284 8 8.0161 7.98601 8.08386 7.95892C8.15162 7.93182 8.21238 7.89223 8.26223 7.84267L11.8335 4.38932C11.9406 4.28419 12 4.14649 12 4.00356C12 3.86063 11.9406 3.72293 11.8335 3.6178Z" />
                            </svg>
                        </Link>
                    </div>
                ) : null}
            </motion.div>
        </section>
    )
}
